import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { decodeURIComponent } from "@lib/utils";
import { useToast } from "@customHooks/useToast";
import { Button } from "@components/shadcn-ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/shadcn-ui/tabs";
import { Card, CardContent } from "@components/shadcn-ui/card";
import { Users, MapPin, UserCheck, Trash2, Briefcase } from "lucide-react";
import { getGroup, removeMember, updateCoordinator } from "features/groups/services/groupsService";
import { Group, GroupMember } from "features/groups/types";
import { encodeURIComponent as encodeURI } from "@lib/utils";

export const GroupDetailsPage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const city = searchParams.get("city");
    const [group, setGroup] = useState<Group | null>(null);
    const navigate = useNavigate();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);

    const decodedGroupId = decodeURIComponent(id!).toString();
    const decodedCity = decodeURIComponent(city!).toString();


    useEffect(() => {
        const fetchGroupDetails = async () => {
            if (!id || !city) return;
            setIsLoading(true);
            try {
                const groupData = await getGroup(decodedGroupId, decodedCity);
                setGroup(groupData);
            } catch (err) {
                console.error("Failed to fetch group details:", err);
                toast({
                    variant: "destructive",
                    description: "Failed to load group details",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchGroupDetails();
    }, [id, city, decodedGroupId, decodedCity, toast]);

    const handleRemoveMember = async (phoneNumber: string) => {
        if (!group) return;

        try {
            await removeMember(group.id, phoneNumber, group.city);
            toast({
                description: "Member removed successfully",
            });
            // Refresh group data
            const updatedGroup = await getGroup(decodedGroupId, decodedCity);
            setGroup(updatedGroup);
        } catch (error) {
            toast({
                variant: "destructive",
                description: "Failed to remove member",
            });
        }
    };

    const handleMakeCoordinator = async (memberPhoneNumber: string) => {
        if (!group) return;

        try {
            await updateCoordinator({
                id: group.id,
                city: group.city,
                phoneNumber: memberPhoneNumber
            });
            toast({
                description: "Coordinator updated successfully",
            });
            // Refresh group data
            const updatedGroup = await getGroup(decodedGroupId, decodedCity);
            setGroup(updatedGroup);
        } catch (error) {
            toast({
                variant: "destructive",
                description: "Failed to update coordinator",
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-10">
                <p className="text-gray-500">Loading group details...</p>
            </div>
        );
    }

    if (!group) {
        return (
            <div className="flex items-center justify-center p-10">
                <p className="text-gray-500">Group not found</p>
            </div>
        );
    }

    return (
        <div className="h-fit">
            <strong className="block mt-0 mb-2 text-md bg-blue-200 p-2 pl-4 rounded-sm">
                Group Details
            </strong>

            <Card className="mb-4">
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="h-5 w-5 text-blue-600" />
                                <span className="font-semibold">Group Name:</span>
                            </div>
                            <p className="text-gray-700 ml-7">{group.name}</p>
                        </div>

                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin className="h-5 w-5 text-blue-600" />
                                <span className="font-semibold">City:</span>
                            </div>
                            <p className="text-gray-700 ml-7">{group.city}</p>
                        </div>

                        {group.coordinator && (
                            <div className="md:col-span-2">
                                <div className="flex items-center gap-2 mb-2">
                                    <UserCheck className="h-5 w-5 text-blue-600" />
                                    <span className="font-semibold">Coordinator:</span>
                                </div>
                                <p className="text-gray-700 ml-7">
                                    {group.coordinator.contact.firstName} {group.coordinator.contact.lastName} - {group.coordinator.contact.phoneNumber}
                                </p>
                            </div>
                        )}

                        {group.bdManager && (
                            <div className="md:col-span-2">
                                <div className="flex items-center gap-2 mb-2">
                                    <Briefcase className="h-5 w-5 text-blue-600" />
                                    <span className="font-semibold">BD Manager:</span>
                                </div>
                                <p className="text-gray-700 ml-7">
                                    {group.bdManager.name} - {group.bdManager.phoneNumber}
                                </p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Tabs defaultValue="members" className="w-full">
                <TabsList className="w-full">
                    <TabsTrigger value="members" className="flex-1">
                        Members ({group.members?.length || 0})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="members">
                    <div className="mt-4">
                        <div className="flex justify-between items-center bg-blue-200 p-2 rounded-sm mb-3">
                            <strong className="text-md">Group Members</strong>
                            <Button
                                onClick={() => navigate(`/dashboard/groups/${id}/add-member?city=${city}`)}
                                className="bg-primary hover:bg-primary/90 h-8 text-sm"
                            >
                                Add Member
                            </Button>
                        </div>

                        {group.members && group.members.length > 0 ? (
                            <div className="space-y-2">
                                {group.members
                                    .filter(member => member.contact) // Filter out members without contact
                                    .map((member: GroupMember) => (
                                        <Card
                                            key={member.contact.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => navigate(`/dashboard/lead-details/${encodeURI(member.contact.phoneNumber)}`)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-blue-100 p-2 rounded-full">
                                                                <Users className="h-4 w-4 text-blue-600" />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold">
                                                                    {member.contact.firstName} {member.contact.lastName}
                                                                    {member.role === "COORDINATOR" && (
                                                                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                                            Coordinator
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                <p className="text-sm text-gray-600">{member.contact.phoneNumber}</p>
                                                                <p className="text-xs text-gray-500">Status: {member.status}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                        {member.role !== "COORDINATOR" && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleMakeCoordinator(member.contact.phoneNumber)}
                                                            >
                                                                Make Coordinator
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => handleRemoveMember(member.contact.phoneNumber)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                            </div>
                        ) : (
                            <div className="flex justify-center p-10">
                                <p className="text-gray-500">No members in this group</p>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};
